import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, ShieldAlert, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Inline House badge for a card, plus the pledge / change-House flow.
 *
 * • Renders the current House pill (crest + name in the House color).
 * • If the card is Unhoused, shows the prominent "not pledged" warning
 *   with a clear "Pledge House" call to action.
 * • Opens a picker dialog listing every seeded House; changing away from
 *   a real House requires an explicit wipe confirmation matching the
 *   backend's 409-guarded contract.
 */
export function HouseBadge({ card, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const houseKey = card?.house_key || "unhoused";
  const { data } = useQuery({
    queryKey: ["houses"],
    queryFn: async () => (await api.get("/houses")).data,
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const houses = data?.houses || [];
  const current = houses.find((h) => h.key === houseKey) || {
    key: "unhoused", name: "Unhoused", color: "#94a3b8", crest: "🪦",
  };
  const isUnhoused = houseKey === "unhoused";

  return (
    <div className={className} data-testid={`house-badge-${card.id}`}>
      <div className="flex items-center gap-2">
        <div
          className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em]"
          style={{ borderColor: `${current.color}55`, background: `${current.color}12`, color: current.color }}
          data-testid={`house-badge-${card.id}-current`}
        >
          <span className="text-sm leading-none">{current.crest}</span>
          {current.name}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid={`house-badge-${card.id}-open-picker`}
          className="h-7 px-2 text-[11px] border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
        >
          <Crown className="h-3 w-3 mr-1" />
          {isUnhoused ? "Pledge House" : "Change House"}
        </Button>
      </div>

      {isUnhoused && (
        <div
          className="mt-2 rounded-lg border border-amber-300/40 bg-amber-400/[0.06] px-3 py-2 flex items-start gap-2"
          data-testid={`house-badge-${card.id}-unhoused-warning`}
        >
          <ShieldAlert className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
          <div className="text-[11.5px] text-amber-100/85 leading-snug">
            This card has not pledged allegiance to a House. Pick a banner so your wins start counting for someone.
          </div>
        </div>
      )}

      <HousePickerDialog card={card} open={open} onOpenChange={setOpen} houses={houses} current={current} />
    </div>
  );
}

function HousePickerDialog({ card, open, onOpenChange, houses, current }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState(current?.key || "unhoused");
  const [confirmed, setConfirmed] = useState(false);
  const isChangingAway = current?.key && current.key !== "unhoused" && target !== current.key;

  const pledge = useMutation({
    mutationFn: async () => (await api.post(`/houses/pledge/${card.id}`, {
      house_key: target,
      confirmed_wipe: isChangingAway ? confirmed : true,
    })).data,
    onSuccess: (r) => {
      if (r.unchanged) {
        toast("No change — card is still pledged to " + current.name, { icon: "🏛" });
      } else {
        toast.success(`Pledged to ${r.house_name || target}`);
      }
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["card", card.id] });
      qc.invalidateQueries({ queryKey: ["collection"] });
      qc.invalidateQueries({ queryKey: ["houses"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, "Pledge failed")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid={`house-picker-${card.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Crown className="h-4 w-4 text-amber-300" />
            Choose your House
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Cards earn House achievements only while pledged. Leaving a House wipes its contribution — the new House starts you at zero.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
          {houses.map((h) => {
            const active = h.key === target;
            const tint = h.color || "#94a3b8";
            return (
              <button
                key={h.key}
                type="button"
                data-testid={`house-picker-${card.id}-option-${h.key}`}
                onClick={() => { setTarget(h.key); setConfirmed(false); }}
                className="text-left rounded-lg border p-2 transition-colors"
                style={{
                  borderColor: active ? tint : "rgba(255,255,255,0.1)",
                  background: active ? `${tint}18` : "rgba(255,255,255,0.02)",
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-base leading-none">{h.crest || "🏛"}</span>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-semibold truncate">{h.name}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 truncate">{h.motto}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {isChangingAway && (
          <div className="rounded-lg border border-red-400/40 bg-red-400/[0.07] px-3 py-2" data-testid={`house-picker-${card.id}-wipe-notice`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-300 mt-0.5 shrink-0" />
              <div className="text-[12px] text-red-100/90 leading-snug">
                Switching away from <strong>{current.name}</strong> will forfeit this card&apos;s contributions and Trophy Room slots for that House.
                Achievements do not transfer — you start over in the new House.
              </div>
            </div>
            <label className="mt-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/70">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="accent-red-400"
                data-testid={`house-picker-${card.id}-confirm-wipe`}
              />
              I understand and want to switch anyway.
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid={`house-picker-${card.id}-cancel`} className="text-white/70">Cancel</Button>
          <Button
            onClick={() => pledge.mutate()}
            disabled={pledge.isPending || target === current.key || (isChangingAway && !confirmed)}
            data-testid={`house-picker-${card.id}-confirm`}
            className="bg-amber-400 text-black hover:bg-amber-300"
          >
            {pledge.isPending ? "Pledging…" : `Pledge to ${houses.find((h) => h.key === target)?.name || target}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
